import { CSSResultGroup, TemplateResult, html } from 'lit';
import { OutlineElement, SlotsController } from '@phase2/outline-core';
import { AdoptedStyleSheets } from '@phase2/outline-adopted-stylesheets-controller';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import { ResizeController } from './../../../controllers/resize-controller';
import { PCAccordionBaselinePanel } from './pc-accordion-baseline-panel/pc-accordion-baseline-panel';
import componentStyles from './pc-accordion-baseline.css.lit';
import globalStyles from './pc-accordion-baseline.lightDom.css.lit';

/**
 * The AccordionBaseline component
 * @element pc-accordion-baseline
 */
@customElement('pc-accordion-baseline')
export class PCAccordionBaseline extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];
  slots = new SlotsController(this);

  adoptedStylesheets: AdoptedStyleSheets;
  resizeController: ResizeController;

  /**
   * Reference to <pc-accordion-panel> in panels slot.
   */
  @queryAssignedElements({ slot: 'panels' })
  panels: PCAccordionBaselinePanel[];

  @property({ type: String, reflect: true, attribute: 'layout' })
  layout: 'equal' | 'main-right' = 'equal';

  /**
   * Width at which layout changes from horizontal to vertical
   */
  @property({
    type: String,
    reflect: true,
    converter: Number,
    attribute: 'vertical-trigger-width',
  })
  verticalTriggerWidth = 1024;

  isMobile: boolean;

  connectedCallback() {
    super.connectedCallback();
    this.adoptedStylesheets = new AdoptedStyleSheets(globalStyles);
    this.addController(this.adoptedStylesheets);

    this.resizeController = new ResizeController(this, {
      breakpoints: [this.verticalTriggerWidth],
    });
  }

  render(): TemplateResult {
    this.isMobile = this.resizeController.currentBreakpointRange === 0;

    let hasIntro = false;

    if (
      this.slots.exist('intro-heading') ||
      this.slots.exist('intro-description') ||
      this.slots.exist('intro-image')
    ) {
      hasIntro = true;
    }
    return html`
      <section class="accordion ${hasIntro ? 'has-intro' : ''} ${this.layout}">
        ${hasIntro
          ? html`
              <div class="accordion--intro">
                ${this.isMobile
                  ? this.slots.conditionalSlot('intro-image', false)
                  : null}
                ${this.slots.conditionalSlot('intro-heading', false)}
                ${this.slots.conditionalSlot('intro-description', false)}
                ${!this.isMobile
                  ? this.slots.conditionalSlot('intro-image', false)
                  : null}
              </div>
            `
          : ''}
        <div class="accordion--panels" @keydown=${this.handleKeyboardNav}>
          <slot name="panels"></slot>
        </div>
      </section>
    `;
  }

  /**
   * @param event
   * @returns void
   * Adds up/down arrow keyboard navigation.
   * TODO: try to refactor to something simpler.
   */
  handleKeyboardNav(event: KeyboardEvent) {
    let index = 0;
    const panels = this.panels;
    const start = Array.prototype.indexOf.call(
      panels,
      event.target as HTMLElement
    );

    // bail on wrong key
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') {
      return;
    }
    if (event.key === 'ArrowDown') {
      if (panels?.length && start + 1 > panels?.length - 1) {
        index = 0;
      } else index = start + 1;
    }
    if (event.key === 'ArrowUp') {
      if (panels?.length && start - 1 < 0) {
        index = panels?.length - 1;
      } else index = start - 1;
    }
    const focusTarget = panels?.[index] as unknown as HTMLElement;
    focusTarget.shadowRoot?.querySelector('button')?.focus();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'pc-accordion-baseline': PCAccordionBaseline;
  }
}
